import { z } from 'zod'

import {
  getLoginUrlForRedirectPath,
  getSignupUrlForRedirectPath,
} from '~publish/helpers/publicAuthUrls'

export const GUEST_DRAFT_HANDOFF_STORAGE_KEY = 'buffer:publish:guest-handoff'
export const GUEST_DRAFT_HANDOFF_QUERY_PARAM = 'guestHandoff'

const STORAGE_VERSION = 1

const GuestDraftComposerHandoffIntentSchema = z
  .object({
    version: z.literal(STORAGE_VERSION),
    id: z.string().uuid(),
    action: z.enum(['saveDraft', 'schedule']),
    initiatingDraftId: z.string().min(1),
    scheduledAt: z.number().int().positive().optional(),
    createdAt: z.number().int().nonnegative(),
    importedInitiatingContentItemId: z.string().min(1).optional(),
  })
  .strict()

export const GUEST_DRAFT_HANDOFF_LANDING_PATH = '/schedule?tab=drafts'

const GuestDraftSignupReturnPathSchema = z
  .string()
  .max(2048)
  .refine(
    (path) =>
      path.startsWith('/') && !path.startsWith('//') && !path.includes('\\'),
  )

const GuestDraftSignupHandoffIntentSchema = z
  .object({
    version: z.literal(STORAGE_VERSION),
    id: z.string().uuid(),
    action: z.literal('signUp'),
    returnTo: GuestDraftSignupReturnPathSchema,
    createdAt: z.number().int().nonnegative(),
  })
  .strict()

const GuestDraftHandoffIntentSchema = z.discriminatedUnion('action', [
  GuestDraftComposerHandoffIntentSchema,
  GuestDraftSignupHandoffIntentSchema,
])

export type GuestDraftSignupReturnPath = z.infer<
  typeof GuestDraftSignupReturnPathSchema
>
export type GuestDraftComposerHandoffIntent = Readonly<
  z.infer<typeof GuestDraftComposerHandoffIntentSchema>
>
type GuestDraftSignupHandoffIntent = Readonly<
  z.infer<typeof GuestDraftSignupHandoffIntentSchema>
>
export type GuestDraftHandoffIntent =
  | GuestDraftComposerHandoffIntent
  | GuestDraftSignupHandoffIntent
export type GuestDraftHandoffAction = GuestDraftHandoffIntent['action']
export type GuestDraftComposerHandoffAction =
  GuestDraftComposerHandoffIntent['action']

type GuestDraftComposerHandoffInput = Pick<
  GuestDraftComposerHandoffIntent,
  'action' | 'initiatingDraftId' | 'scheduledAt'
>
type GuestDraftSignupHandoffInput = Pick<
  GuestDraftSignupHandoffIntent,
  'action' | 'returnTo'
>

export type ReadGuestDraftHandoffResult =
  | Readonly<{ status: 'ready'; intent: GuestDraftHandoffIntent }>
  | Readonly<{ status: 'empty' }>
  | Readonly<{ status: 'invalid' }>
  | Readonly<{ status: 'read-failed' }>

export type CreateGuestDraftHandoffResult<
  TIntent extends GuestDraftHandoffIntent = GuestDraftHandoffIntent,
> =
  | Readonly<{ status: 'saved'; intent: TIntent }>
  | Readonly<{ status: 'write-failed' }>

export type UpdateGuestDraftHandoffResult =
  | Readonly<{ status: 'saved'; intent: GuestDraftHandoffIntent }>
  | Readonly<{ status: 'not-found' }>
  | Readonly<{ status: 'invalid' }>
  | Readonly<{ status: 'read-failed' }>
  | Readonly<{ status: 'write-failed' }>

export type ClearGuestDraftHandoffResult =
  | Readonly<{ status: 'cleared' }>
  | Readonly<{ status: 'not-found' }>
  | Readonly<{ status: 'read-failed' }>
  | Readonly<{ status: 'write-failed' }>

type StorageAdapter = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

type GuestDraftHandoffStoreOptions = Readonly<{
  storage: StorageAdapter
  now: () => number
  generateId: () => string
}>

export type GuestDraftHandoffStore = Readonly<{
  read: (intentId?: string) => ReadGuestDraftHandoffResult
  create: {
    (
      input: GuestDraftComposerHandoffInput,
    ): CreateGuestDraftHandoffResult<GuestDraftComposerHandoffIntent>
    (
      input: GuestDraftSignupHandoffInput,
    ): CreateGuestDraftHandoffResult<GuestDraftSignupHandoffIntent>
  }
  markInitiatingDraftImported: (
    intentId: string,
    contentItemId: string,
  ) => UpdateGuestDraftHandoffResult
  clear: (intentId?: string) => ClearGuestDraftHandoffResult
}>

function parseIntent(serializedIntent: string): GuestDraftHandoffIntent | null {
  let parsedIntent: unknown
  try {
    parsedIntent = JSON.parse(serializedIntent)
  } catch {
    return null
  }

  const result = GuestDraftHandoffIntentSchema.safeParse(parsedIntent)
  return result.success ? result.data : null
}

export function createGuestDraftHandoffStore({
  storage,
  now,
  generateId,
}: GuestDraftHandoffStoreOptions): GuestDraftHandoffStore {
  const read = (intentId?: string): ReadGuestDraftHandoffResult => {
    let serializedIntent: string | null
    try {
      serializedIntent =
        (intentId
          ? storage.getItem(`${GUEST_DRAFT_HANDOFF_STORAGE_KEY}:${intentId}`)
          : null) ?? storage.getItem(GUEST_DRAFT_HANDOFF_STORAGE_KEY)
    } catch {
      return { status: 'read-failed' }
    }

    if (serializedIntent === null) return { status: 'empty' }

    const intent = parseIntent(serializedIntent)
    if (!intent) return { status: 'invalid' }
    if (intentId && intent.id !== intentId) return { status: 'empty' }
    return { status: 'ready', intent }
  }

  const write = (
    intent: GuestDraftHandoffIntent,
    makeCurrent = false,
  ): CreateGuestDraftHandoffResult => {
    try {
      const serializedIntent = JSON.stringify(intent)
      storage.setItem(
        `${GUEST_DRAFT_HANDOFF_STORAGE_KEY}:${intent.id}`,
        serializedIntent,
      )
      const current = read()
      if (
        makeCurrent ||
        (current.status === 'ready' && current.intent.id === intent.id)
      ) {
        storage.setItem(GUEST_DRAFT_HANDOFF_STORAGE_KEY, serializedIntent)
      }
      return { status: 'saved', intent }
    } catch {
      return { status: 'write-failed' }
    }
  }

  function create(
    input: GuestDraftComposerHandoffInput,
  ): CreateGuestDraftHandoffResult<GuestDraftComposerHandoffIntent>
  function create(
    input: GuestDraftSignupHandoffInput,
  ): CreateGuestDraftHandoffResult<GuestDraftSignupHandoffIntent>
  function create(
    input: GuestDraftComposerHandoffInput | GuestDraftSignupHandoffInput,
  ): CreateGuestDraftHandoffResult {
    const result = GuestDraftHandoffIntentSchema.safeParse({
      ...input,
      version: STORAGE_VERSION,
      id: generateId(),
      createdAt: now(),
    })

    if (!result.success) return { status: 'write-failed' }

    return write(result.data, true)
  }

  return {
    read,
    create,
    markInitiatingDraftImported: (intentId, contentItemId) => {
      const readResult = read(intentId)
      if (readResult.status !== 'ready') {
        return readResult.status === 'empty'
          ? { status: 'not-found' }
          : readResult
      }
      if (readResult.intent.id !== intentId) return { status: 'not-found' }

      const result = GuestDraftHandoffIntentSchema.safeParse({
        ...readResult.intent,
        importedInitiatingContentItemId: contentItemId,
      })
      if (!result.success) return { status: 'invalid' }

      return write(result.data)
    },
    clear: (intentId) => {
      const readResult = read(intentId)
      if (readResult.status === 'read-failed') return readResult
      if (readResult.status !== 'ready') {
        return { status: 'not-found' }
      }

      try {
        storage.removeItem(
          `${GUEST_DRAFT_HANDOFF_STORAGE_KEY}:${readResult.intent.id}`,
        )
        const current = read()
        if (current.status === 'read-failed') return current
        if (
          current.status === 'ready' &&
          current.intent.id === readResult.intent.id
        ) {
          storage.removeItem(GUEST_DRAFT_HANDOFF_STORAGE_KEY)
        }
        return { status: 'cleared' }
      } catch {
        return { status: 'write-failed' }
      }
    },
  }
}

let browserGuestDraftHandoffStore: GuestDraftHandoffStore | undefined

export function getBrowserGuestDraftHandoffStore(): GuestDraftHandoffStore {
  if (browserGuestDraftHandoffStore) return browserGuestDraftHandoffStore

  if (typeof window === 'undefined') {
    throw new Error('The guest draft handoff store requires a browser.')
  }

  browserGuestDraftHandoffStore = createGuestDraftHandoffStore({
    storage: window.localStorage,
    now: Date.now,
    generateId: () => crypto.randomUUID(),
  })

  return browserGuestDraftHandoffStore
}

export function getGuestDraftHandoffRedirectPath(intentId: string): string {
  const searchParams = new URLSearchParams({
    tab: 'drafts',
    [GUEST_DRAFT_HANDOFF_QUERY_PARAM]: intentId,
  })

  return `/schedule?${searchParams.toString()}`
}

export type GuestAccountDestination = 'login' | 'signup'

export function getGuestAccountUrl({
  destination,
  hasGuestDrafts,
  fallbackPath,
  returnTo = GUEST_DRAFT_HANDOFF_LANDING_PATH,
  handoffStore,
}: {
  destination: GuestAccountDestination
  hasGuestDrafts: boolean
  fallbackPath: string
  returnTo?: GuestDraftSignupReturnPath
  handoffStore?: GuestDraftHandoffStore
}): string | undefined {
  let redirectPath = fallbackPath
  if (hasGuestDrafts) {
    const result = (handoffStore ?? getBrowserGuestDraftHandoffStore()).create({
      action: 'signUp',
      returnTo,
    })
    if (result.status !== 'saved') return undefined
    redirectPath = getGuestDraftHandoffRedirectPath(result.intent.id)
  }

  return destination === 'login'
    ? getLoginUrlForRedirectPath(redirectPath)
    : getSignupUrlForRedirectPath(redirectPath)
}
